import { readonly, ref } from 'vue'

export type AppDialogTone = 'default' | 'danger'

export interface AppConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  tone?: AppDialogTone
}

export interface AppPromptOptions {
  title: string
  label: string
  message?: string
  initialValue?: string
  placeholder?: string
  confirmText?: string
  cancelText?: string
  maxLength?: number
  tone?: AppDialogTone
}

interface BaseDialogRequest {
  id: number
  title: string
  message: string
  confirmText: string
  cancelText: string
  tone: AppDialogTone
}

export interface ConfirmDialogRequest extends BaseDialogRequest {
  kind: 'confirm'
  resolve: (value: boolean) => void
}

export interface PromptDialogRequest extends BaseDialogRequest {
  kind: 'prompt'
  label: string
  initialValue: string
  placeholder: string
  maxLength: number | null
  resolve: (value: string | null) => void
}

export type AppDialogRequest = ConfirmDialogRequest | PromptDialogRequest

const activeDialog = ref<AppDialogRequest | null>(null)
const queue: AppDialogRequest[] = []
let nextDialogId = 1

function showNextDialog(): void {
  if (activeDialog.value || queue.length === 0) return
  activeDialog.value = queue.shift() ?? null
}

function enqueueDialog<T>(request: AppDialogRequest): Promise<T> {
  queue.push(request)
  showNextDialog()
  return new Promise((resolve) => {
    request.resolve = resolve as AppDialogRequest['resolve']
  })
}

function settleDialog<T>(value: T): void {
  const current = activeDialog.value
  if (!current) return
  activeDialog.value = null
  ;(current.resolve as (result: T) => void)(value)
  showNextDialog()
}

export function useAppDialog(): {
  confirm: (options: AppConfirmOptions) => Promise<boolean>
  prompt: (options: AppPromptOptions) => Promise<string | null>
} {
  return {
    confirm(options) {
      return enqueueDialog<boolean>({
        id: nextDialogId++,
        kind: 'confirm',
        title: options.title,
        message: options.message,
        confirmText: options.confirmText ?? '确定',
        cancelText: options.cancelText ?? '取消',
        tone: options.tone ?? 'default',
        resolve: () => undefined,
      })
    },
    prompt(options) {
      return enqueueDialog<string | null>({
        id: nextDialogId++,
        kind: 'prompt',
        title: options.title,
        message: options.message ?? '',
        label: options.label,
        initialValue: options.initialValue ?? '',
        placeholder: options.placeholder ?? '',
        confirmText: options.confirmText ?? '确定',
        cancelText: options.cancelText ?? '取消',
        maxLength: options.maxLength ?? null,
        tone: options.tone ?? 'default',
        resolve: () => undefined,
      })
    },
  }
}

export function useAppDialogHost(): {
  dialog: Readonly<typeof activeDialog>
  confirm: () => void
  cancel: () => void
  submitPrompt: (value: string) => void
} {
  return {
    dialog: readonly(activeDialog),
    confirm() {
      settleDialog(true)
    },
    cancel() {
      const current = activeDialog.value
      settleDialog(current?.kind === 'prompt' ? null : false)
    },
    submitPrompt(value) {
      settleDialog(value)
    },
  }
}
